import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, Validators, FormGroup } from '@angular/forms';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { Select } from 'primeng/select';
import { AncillaryService } from '@services/ancillary.service';
import { AccountingReportService } from '@services/accounting-report-service.service';
import { FareType } from '@shared/interfaces/ancillary.interface';

export type AncillaryDialogMode = 'add' | 'edit';

export interface AncillaryFormModel {
    id: number | null;
    ancillaryKey: string;
    providerCode: string | null;
    fareTypeName: string | null;
    categoryName: string | null;
    mandatorySelection: boolean;
    status: 'ACTIVE' | 'FORCED' | 'PASSIVE' | '';
    titlesByLanguage: { [k: string]: string };
}

@Component({
    selector: 'app-ancillary-create-edit',
    standalone: true,
    imports: [CommonModule, FormsModule, ReactiveFormsModule, InputTextModule, ButtonModule, CheckboxModule, Select],
    templateUrl: './ancillary-create-edit.component.html'
})
export class AncillaryCreateEditComponent implements OnChanges {
    @Input() mode: AncillaryDialogMode = 'add';
    @Input() data: Partial<AncillaryFormModel> | null = null;

    @Output() cancel = new EventEmitter<void>();
    @Output() save = new EventEmitter<AncillaryFormModel>();

    ancillaryService = inject(AncillaryService);
    accountingReportService = inject(AccountingReportService);

    providers: { code: string; explanation: string }[] = [];
    fareTypes: FareType[] = [];
    categories: string[] = [];
    submitted = false;

    fb = inject(FormBuilder);
    formGroup: FormGroup = this.fb.group({
        id: [null],
        ancillaryKey: ['', [Validators.required, Validators.maxLength(255)]],
        providerCode: [null, Validators.required],
        fareTypeName: [null, Validators.required],
        categoryName: [null, Validators.required],
        mandatorySelection: [false],
        status: ['', Validators.required],
        titlesByLanguage: this.fb.group({
            TR: ['', [Validators.maxLength(255)]],
            NL: ['', [Validators.maxLength(255)]],
            DE: ['', [Validators.maxLength(255)]],
            EN: ['', [Validators.required, Validators.maxLength(255)]]
        })
    });

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['data']) {
            if (this.data) {
                this.formGroup.patchValue({
                    id: this.data.id ?? null,
                    ancillaryKey: this.data.ancillaryKey ?? '',
                    providerCode: this.data.providerCode ?? null,
                    fareTypeName: this.data.fareTypeName ?? null,
                    categoryName: this.data.categoryName ?? null,
                    mandatorySelection: this.data.mandatorySelection ?? false,
                    status: this.data.status ?? '',
                    titlesByLanguage: {
                        TR: this.data.titlesByLanguage?.['TR'] ?? '',
                        NL: this.data.titlesByLanguage?.['NL'] ?? '',
                        DE: this.data.titlesByLanguage?.['DE'] ?? '',
                        EN: this.data.titlesByLanguage?.['EN'] ?? ''
                    }
                });
            } else {
                this.formGroup.reset();
            }
            this.submitted = false;
        }
    }

    ngOnInit(): void {
        this.loadLookups();
    }

    loadLookups(): void {
        this.accountingReportService.getProviders().subscribe({
            next: (res) => (this.providers = res || []),
            error: () => (this.providers = [])
        });
        this.ancillaryService.getFareTypes().subscribe({
            next: (res) => (this.fareTypes = res || []),
            error: () => (this.fareTypes = [])
        });
        this.ancillaryService.getCategories().subscribe({
            next: (res) => (this.categories = (res || []).map((c: any) => c?.name).filter((x: any) => !!x)),
            error: () => (this.categories = [])
        });
    }

    onCancel(): void {
        this.cancel.emit();
    }

    onSave(): void {
        this.submitted = true;
        if (this.formGroup.invalid) {
            this.formGroup.markAllAsTouched();
            return;
        }
        const v = this.formGroup.getRawValue() as any;
        const form: AncillaryFormModel = {
            id: v.id,
            ancillaryKey: v.ancillaryKey,
            providerCode: v.providerCode,
            fareTypeName: v.fareTypeName,
            categoryName: v.categoryName,
            mandatorySelection: !!v.mandatorySelection,
            status: v.status,
            titlesByLanguage: {
                TR: v.titlesByLanguage?.TR ?? '',
                NL: v.titlesByLanguage?.NL ?? '',
                DE: v.titlesByLanguage?.DE ?? '',
                EN: v.titlesByLanguage?.EN ?? ''
            }
        };
        this.save.emit(form);
    }
}
